import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowUp,
  BarChart3,
  Brain,
  Calendar,
  ClipboardList,
  Clock3,
  FileText,
  Mail,
  Plus,
  Target,
  Users,
  X,
} from 'lucide-react';
import type { AssistantPrompt } from '../types';
import type { AIAssistantRecentItem } from './AIAssistant';
import { getAssistantMotionTransition } from './internal/assistantMotion';
import { schoolShellAssistantStyleContract } from './internal/schoolShellAssistantStyleContract';
import { autoResizeTextarea } from '../ui/impl/textareaAutosize.js';

const MAX_STAGED_FILES = 3;

export type AIAssistantPrototypeRouteCanvasProps = {
  assistantLabel?: string;
  welcomeHeading?: string;
  promptPlaceholder?: string;
  quickActions?: readonly AssistantPrompt[];
  recentItems?: readonly AIAssistantRecentItem[];
  modelLabel?: string;
  modelMenu?: React.ReactNode;
  footerNote?: string;
  onSubmit?: (question: string) => boolean | void;
  onAttachFile?: (file: File) => void | Promise<void>;
  onRecentItemClick?: (item: AIAssistantRecentItem) => void;
  className?: string;
};

const resolveQuickActionIcon = (
  prompt: AssistantPrompt,
): React.ComponentType<{ size?: number | string; strokeWidth?: number | string }> => {
  if (prompt.icon) {
    return prompt.icon;
  }
  const text = prompt.label.toLowerCase();
  if (text.includes('yield') || text.includes('pipeline') || text.includes('bottleneck')) {
    return BarChart3;
  }
  if (text.includes('interview') || text.includes('schedule')) {
    return Calendar;
  }
  if (text.includes('at-risk') || text.includes('risk') || text.includes('alert')) {
    return AlertTriangle;
  }
  if (text.includes('family') || text.includes('applicant')) {
    return Users;
  }
  if (text.includes('email') || text.includes('message')) {
    return Mail;
  }
  return ClipboardList;
};

const resolveAssistantPromptText = (prompt: AssistantPrompt): string => {
  const text = prompt.prompt?.trim();
  return text && text.length > 0 ? text : prompt.label;
};

export function AIAssistantPrototypeRouteCanvas({
  assistantLabel = 'Assistant',
  welcomeHeading = 'Welcome!',
  promptPlaceholder = 'What would you like to take care of today?',
  quickActions = [],
  recentItems = [],
  modelLabel = 'GPT-4o',
  modelMenu = null,
  footerNote = 'AI-generated responses. Please check for accuracy.',
  onSubmit,
  onAttachFile,
  onRecentItemClick,
  className,
}: AIAssistantPrototypeRouteCanvasProps) {
  const reducedMotion = Boolean(useReducedMotion());
  const [question, setQuestion] = useState('');
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<Array<{ id: string; label: string }>>([]);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const modelButtonRef = useRef<HTMLButtonElement | null>(null);
  const modelMenuRef = useRef<HTMLDivElement | null>(null);

  const routeQuickActions = useMemo(() => quickActions.slice(0, 4), [quickActions]);
  const routeRecentItems = useMemo(() => recentItems.slice(0, 3), [recentItems]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    autoResizeTextarea(textareaRef.current, { autosize: true, minRows: 1 });
  }, [question]);

  useEffect(() => {
    if (typeof window === 'undefined' || !showModelMenu) {
      return undefined;
    }
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (modelButtonRef.current?.contains(target)) {
        return;
      }
      if (modelMenuRef.current?.contains(target)) {
        return;
      }
      setShowModelMenu(false);
    };
    window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [showModelMenu]);

  const handlePromptFill = (value: string) => {
    setQuestion(value);
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
      return;
    }
    textareaRef.current?.focus();
  };

  const submitPromptValue = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }
    const handled = onSubmit?.(trimmed);
    if (handled === false) {
      handlePromptFill(trimmed);
      return;
    }
    setQuestion('');
    setShowModelMenu(false);
  };

  const handleSubmit = () => {
    submitPromptValue(question);
  };

  const handleAttachInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    setStagedFiles((current) => {
      const next = [...current, {
        id: `${file.name}-${file.size}-${current.length + 1}`,
        label: file.name,
      }];
      return next.slice(0, MAX_STAGED_FILES);
    });
    if (onAttachFile) {
      await onAttachFile(file);
    }
  };

  const modelButtonRect = modelButtonRef.current?.getBoundingClientRect() ?? null;
  const modelMenuNode = showModelMenu && modelMenu && modelButtonRect ? (
    <motion.div
      ref={modelMenuRef}
      className={schoolShellAssistantStyleContract.routeCanvasModelMenuClassName}
      style={{
        top: modelButtonRect.bottom + 8,
        right: window.innerWidth - modelButtonRect.right,
        zIndex: 1200,
      }}
      initial={reducedMotion ? false : { opacity: 0, y: 6 }}
      animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
      exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
      transition={getAssistantMotionTransition('panel', reducedMotion)}
      data-testid="ai-prototype-model-menu"
      onClick={(event) => {
        const target = event.target as HTMLElement | null;
        if (target?.closest('[data-ai-model-option="true"]')) {
          setShowModelMenu(false);
        }
      }}
    >
      {modelMenu}
    </motion.div>
  ) : null;

  return (
    <div
      className={[
        schoolShellAssistantStyleContract.routeCanvasRootClassName,
        className ?? '',
      ].join(' ')}
      data-testid="ai-prototype-welcome-view"
      data-ai-route-home-canvas="true"
      data-ai-assistant-label={assistantLabel}
    >
      <div className={schoolShellAssistantStyleContract.routeCanvasWelcomeContentClassName}>
        <div className={schoolShellAssistantStyleContract.welcomeIconClassName}>
          <Brain size={32} />
        </div>
        <h1
          className={`${schoolShellAssistantStyleContract.welcomeHeadingClassName} mb-8`}
        >
          {welcomeHeading}
        </h1>

        <div className="w-full max-w-[600px]">
          {stagedFiles.length > 0 ? (
            <div className="mb-2 flex flex-wrap gap-1">
              {stagedFiles.map((file) => (
                <span
                  key={file.id}
                  className={schoolShellAssistantStyleContract.routeCanvasStagedFileClassName}
                >
                  <FileText size={12} className="text-text-secondary" />
                  <span className="max-w-[120px] truncate text-irongrey">{file.label}</span>
                  <button
                    type="button"
                    className={schoolShellAssistantStyleContract.routeCanvasStagedFileRemoveClassName}
                    onClick={() => {
                      setStagedFiles((current) => current.filter((entry) => entry.id !== file.id));
                    }}
                    aria-label={`Remove ${file.label}`}
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          ) : null}

          <div
            className={schoolShellAssistantStyleContract.welcomeInputShellClassName}
          >
            <textarea
              ref={textareaRef}
              value={question}
              onChange={(event) => {
                setQuestion(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  handleSubmit();
                }
              }}
              rows={1}
              placeholder={promptPlaceholder}
              className={schoolShellAssistantStyleContract.welcomeInputClassName}
              style={{ scrollbarWidth: 'none' }}
              data-testid="ai-prototype-welcome-input"
            />
            <div className="flex items-center justify-between px-3 pb-3">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(event) => {
                  void handleAttachInputChange(event);
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={schoolShellAssistantStyleContract.welcomeAttachClassName}
                aria-label="Add file"
                data-testid="ai-prototype-welcome-attach"
              >
                <Plus size={16} />
              </button>
              <div className="flex items-center gap-1.5">
                <button
                  ref={modelButtonRef}
                  type="button"
                  onClick={() => setShowModelMenu((current) => !current)}
                  className={schoolShellAssistantStyleContract.welcomeModelButtonClassName({ active: showModelMenu })}
                  aria-label={`Model: ${modelLabel}`}
                  data-testid="ai-prototype-welcome-model-button"
                >
                  <Target size={17} />
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!question.trim()}
                  className={schoolShellAssistantStyleContract.welcomeSubmitClassName({ enabled: Boolean(question.trim()) })}
                  aria-label="Start assistant"
                  data-testid="ai-prototype-welcome-submit"
                >
                  <ArrowUp size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 w-full max-w-[600px]" data-testid="ai-school-shell-conversation-route-starts">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {routeQuickActions.map((prompt, index) => {
              const Icon = resolveQuickActionIcon(prompt);
              return (
                <button
                  key={prompt.id ?? `${prompt.label}-${index + 1}`}
                  type="button"
                  onClick={() => submitPromptValue(resolveAssistantPromptText(prompt))}
                  className={schoolShellAssistantStyleContract.routeCanvasQuickActionCardClassName}
                  data-testid="ai-school-shell-quick-action"
                  data-ai-prompt-surface="chat"
                  data-ai-prompt-label={prompt.label}
                  data-ai-prompt-prompt={resolveAssistantPromptText(prompt)}
                >
                  <div
                    className={schoolShellAssistantStyleContract.quickActionIconClassName(false)}
                    data-testid="ai-school-shell-quick-action-icon"
                  >
                    <Icon size={20} />
                  </div>
                  <div className="min-w-0">
                    <div
                      className={`truncate ${schoolShellAssistantStyleContract.quickActionLabelClassName(false)}`}
                      data-testid="ai-school-shell-quick-action-label"
                    >
                      {prompt.label}
                    </div>
                    <div className={schoolShellAssistantStyleContract.routeCanvasQuickActionBadgeClassName}>
                      {prompt.badge ?? 'Route-scoped quick action'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 w-full max-w-[600px]" data-testid="ai-school-shell-conversation-recent-items">
          <span className={schoolShellAssistantStyleContract.routeCanvasRecentHeadingClassName}>
            Recent
          </span>
          <div className="mt-2 space-y-0.5">
            {routeRecentItems.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  if (onRecentItemClick) {
                    onRecentItemClick(item);
                    return;
                  }
                  handlePromptFill(item.prompt || item.title);
                }}
                className={schoolShellAssistantStyleContract.routeCanvasRecentItemClassName}
                data-testid="ai-school-shell-recent-item"
              >
                <div
                  className={`h-6 w-1 shrink-0 rounded-full ${schoolShellAssistantStyleContract.routeCanvasRecentAccentClassNames[index % schoolShellAssistantStyleContract.routeCanvasRecentAccentClassNames.length]}`}
                />
                <Clock3 size={14} className="shrink-0 text-text-tertiary transition-colors group-hover:text-primary" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs text-irongrey">{item.title}</div>
                </div>
                <span className={schoolShellAssistantStyleContract.routeCanvasRecentTimestampClassName}>
                  {index === 0 ? 'Just now' : index === 1 ? 'Today' : 'Earlier'}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="pb-4 text-center" data-testid="ai-assistant-route-footer">
        <span className={schoolShellAssistantStyleContract.routeCanvasFooterClassName}>{footerNote}</span>
      </div>

      <AnimatePresence initial={false}>{modelMenuNode}</AnimatePresence>
    </div>
  );
}
